<template>
  <form class="control-panel" action="">
    <h1 class="title">Median Housing Value<br/>California c. 1990</h1>
    <label for="oceanProximity">Ocean Proximity</label>
    <select class="dropdown" name="oceanProximity" id="" v-model="oceanProximity">
      <option value="lessThanHour">Under 1 Hour from Ocean</option>
      <option value="inland">Inland</option>
      <option value="island">Island</option>
      <option value="nearBay">Near Bay</option>
      <option value="nearOcean">Near Ocean</option>
    </select>
    <label for="medianIncome">Median Income</label>
    <span class="value">{{ medianIncomeAsCurrency }}</span>
    <input type="range" class="slider" name="medianIncome" :min="0.5*10000" :max="15*10000" v-model="medianIncome">
    <label for="housingMedianAge">Housing Median Age</label>
    <span class="value">{{ housingMedianAgeAsGroupedDecimal }}</span>
    <input type="range" class="slider" name="housingMedianAge" min="1" max="60" v-model="housingMedianAge">
    <label for="totalBedrooms">Total Bedrooms</label>
    <span class="value">{{ totalBedroomsAsGroupedDecimal }}</span>
    <input type="range" class="slider" name="totalBedrooms" min="2" max="7000" v-model="totalBedrooms">
    <label for="households">Households</label>
    <span class="value">{{ householdsAsGroupedDecimal }}</span>
    <input type="range" class="slider" name="households" min="2" max="5400" v-model="households">
    <label for="totalRooms">Total Rooms</label>
    <span class="value">{{ totalRoomsAsGroupedDecimal }}</span>
    <input type="range" class="slider" name="totalRooms" min="5" max="40000" v-model="totalRooms">
    <label for="population">Population</label>
    <span class="value">{{ populationAsGroupedDecimal }}</span>
    <input type="range" class="slider" name="population" min="3" max="36000" v-model="population">
    <!-- <label for="medianHouseValue">Median House Value</label> -->
    <!-- <p name="medianHouseValue">{{ medianHouseValue }}</p> -->
  </form>
</template>

<script>
import { mapState } from "vuex";
import axios from "axios";
import { INCOME_FACTOR, OceanProximity, Stats, standardScore } from "@/utils/model";

const currency = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 0,
  maximumFractionDigits: 0
});

const groupedDecimal = new Intl.NumberFormat("en-US", {
  style: "decimal"
});

export default {
  name: "ControlPanel",

  data() {
    return {
      housingMedianAge: 29.0,
      totalRooms: 6447.0,
      totalBedrooms: 1012.0,
      population: 2184.0,
      households: 960.0,
      medianIncome: 8.28 * INCOME_FACTOR, // originally 8.2816
      oceanProximity: OceanProximity.LESS_THAN_HOUR,
    };
  },

  computed: {
    ...mapState([
      "longitude",
      "latitude",
      "medianHouseValue"
    ]),

    medianIncomeAsCurrency() {
      return currency.format(this.medianIncome);
    },

    housingMedianAgeAsGroupedDecimal() {
      return groupedDecimal.format(this.housingMedianAge);
    },

    totalBedroomsAsGroupedDecimal() {
      return groupedDecimal.format(this.totalBedrooms);
    },

    householdsAsGroupedDecimal() {
      return groupedDecimal.format(this.households);
    },
    
    totalRoomsAsGroupedDecimal() {
      return groupedDecimal.format(this.totalRooms);
    },
    
    populationAsGroupedDecimal() {
      return groupedDecimal.format(this.population);
    },

    roomsPerHousehold() {
      return this.totalRooms / this.households;
    },

    populationPerHousehold() {
      return this.population / this.households;
    },

    bedroomsPerRoom() {
      return this.totalBedrooms / this.totalRooms;
    },

    oceanProximityVector() {
      return [
        this.oceanProximity === OceanProximity.LESS_THAN_HOUR ? 1 : 0,
        this.oceanProximity === OceanProximity.INLAND ? 1 : 0,
        this.oceanProximity === OceanProximity.ISLAND ? 1 : 0,
        this.oceanProximity === OceanProximity.NEAR_BAY ? 1 : 0,
        this.oceanProximity === OceanProximity.NEAR_OCEAN ? 1 : 0,
      ];
    }
  },

  watch: {
    longitude() {
      this.predict();
    },

    latitude() {
      this.predict();
    },

    housingMedianAge() {
      this.predict();
    },

    totalRooms() {
      this.predict();
    },

    totalBedrooms() {
      this.predict();
    },

    population() {
      this.predict();
    },

    households() {
      this.predict();
    },

    medianIncome() {
      this.predict();
    },

    oceanProximity() {
      this.predict();
    },

    roomsPerHousehold() {
      this.predict();
    },

    populationPerHousehold() {
      this.predict();
    },

    bedroomsPerRoom() {
      this.predict();
    }
  },

  methods: {
    predict() {
      const features = {};
      features["longitude"] = standardScore(this.longitude, Stats.longitude);
      features["latitude"] = standardScore(this.latitude, Stats.latitude);
      features["housing_median_age"] = standardScore(this.housingMedianAge, Stats.housingMedianAge);
      features["total_rooms"] = standardScore(this.totalRooms, Stats.totalRooms);
      features["total_bedrooms"] = standardScore(this.totalBedrooms, Stats.totalBedrooms);
      features["population"] = standardScore(this.population, Stats.population);
      features["households"] = standardScore(this.households, Stats.households);
      features["median_income"] = standardScore(this.medianIncome/INCOME_FACTOR, Stats.medianIncome);
      features["rooms_per_household"] = standardScore(this.roomsPerHousehold, Stats.roomsPerHousehold);
      features["population_per_household"] = standardScore(this.populationPerHousehold, Stats.populationPerHousehold);
      features["bedrooms_per_room"] = standardScore(this.bedroomsPerRoom, Stats.bedroomsPerRoom);
      features["x0_<1H OCEAN"] = this.oceanProximityVector[0];
      features["x0_INLAND"] = this.oceanProximityVector[1];
      features["x0_ISLAND"] = this.oceanProximityVector[2];
      features["x0_NEAR BAY"] = this.oceanProximityVector[3];
      features["x0_NEAR OCEAN"] = this.oceanProximityVector[4];

      // axios.post("http://127.0.0.1:5000/median-house-value", features)
      axios.post("https://median-housing-value.onrender.com/median-house-value", features)
      .then(response => {
        this.$store.commit("SET_MEDIAN_HOUSE_VALUE", response.data);
      })
      .catch(error => {
        console.log(error);
      });
    }
  },

  mounted() {
    this.predict();
  }
};
</script>

<style lang="scss" scoped>
.control-panel {
  padding: 32px;
  display: grid;
  grid-template-columns: 2fr 1fr;
  grid-gap: 8px;
  font-size: 1rem;
  box-shadow: 2px 0 2px rgba(#000, 0.1);
}

.form {
  margin: 40px;
  display: grid;
  grid-template-columns: 3fr 1fr;
  grid-gap: 20px;
}

.slider,
.dropdown {
  grid-column-end: span 2;
  margin-bottom: 40px;
}

.dropdown {
  font-size: 1rem;
}

.value {
  text-align: right;
}

.title {
  grid-column-end: span 2;
  font-weight: bold;
  margin-bottom: 40px;
  line-height: 1.5rem;;
}
</style>